import { useFormAction, useNavigation } from 'react-router';

/**
 * Returns true when the current React Router navigation is for the
 * given form action+method combination. Use to drive loading state on
 * submit buttons / forms without flickering on unrelated navigations.
 *
 * For UI that needs a minimum visible duration (so the spinner doesn't
 * flash and disappear), wrap with {@link useSpinDelay} or use
 * `useDelayedIsPending` directly.
 *
 * @param formAction The action URL to watch. Defaults to the current
 *   route's form action via `useFormAction()`.
 * @param formMethod HTTP method to match. Defaults to `POST`.
 * @param state Which navigation phase counts as "pending". Defaults to
 *   `non-idle` (any in-flight navigation).
 */
export function useIsPending({
  formAction,
  formMethod = 'POST',
  state = 'non-idle',
}: {
  formAction?: string;
  formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  state?: 'submitting' | 'loading' | 'non-idle';
} = {}) {
  const contextualFormAction = useFormAction();
  const navigation = useNavigation();

  const isPendingState =
    state === 'non-idle'
      ? navigation.state !== 'idle'
      : navigation.state === state;
  return (
    isPendingState &&
    navigation.formAction === (formAction ?? contextualFormAction) &&
    navigation.formMethod === formMethod
  );
}
